import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import { CustomConsensusService } from "../custom-consensus.service";
import { USE_FOR } from "src/app/shared/source-table/source-table.component";
import { CcVars } from "../custom-consensus.model";
import { AppService } from "src/app/app.service";
import dayjs from "dayjs";
import { EventsPageDataService } from "src/app/events-page/events-page-data.service";
import { catchError, debounceTime } from "rxjs/operators";
import { throwError } from "rxjs";
import { UntypedFormControl } from "@angular/forms";
import { VAIconRegistryService, infoCircle } from "@va/core-library/icons";

export interface NgModel {
  selectedBrokersByGrid: number[];
  selectedBrokersByDate: number[];
  reaffirmBrokers: number[];
  showOnlyCurrent: boolean;
  sortColumn: string;
  sortDirection: string;
  hideNonCcBrokersInGrid: boolean;
  includeNonCurrentBrokers: boolean;
  includeNonentitledCurrent: boolean;
  includeNonentitledNoncurrent: boolean;
  relativeDays: number;
  customConsensusMode: string;
  dateCustomConsensus: string;
  customConsensusDateType: string;
  customConsensusBrokerType: string;
}

@Component({
  selector: "app-custom-consensus-core",
  templateUrl: "./custom-consensus-core.component.html",
  styleUrls: ["./custom-consensus-core.component.scss"],
  encapsulation: ViewEncapsulation.None,
  host: { class: "custom-consensus-core" },
})
export class CustomConsensusCoreComponent implements OnChanges {
  useFor = USE_FOR.CC_SELECTION;
  activeTabIdx; // activeTab: 0 => by date | 1 => by broker

  @Input() ccVars: CcVars;
  @Input() showHideBrokerCheckbox = true;
  @Input() note = null;
  @Output() ngModelChange: EventEmitter<NgModel> = new EventEmitter();
  @Output() onSave: EventEmitter<NgModel> = new EventEmitter();
  @Output() onCancel: EventEmitter<NgModel> = new EventEmitter();
  ngModel: NgModel = {
    selectedBrokersByGrid: [],
    selectedBrokersByDate: [],
    reaffirmBrokers: [],
    hideNonCcBrokersInGrid: true,
    includeNonCurrentBrokers: false,
    includeNonentitledCurrent: false,
    includeNonentitledNoncurrent: false,
    showOnlyCurrent: false,
    sortColumn: null,
    sortDirection: null,
    relativeDays: null,
    customConsensusMode: null,
    dateCustomConsensus: null,
    customConsensusDateType: null,
    customConsensusBrokerType: null,
  };
  ngModelCopy: NgModel = null;
  customConsensusDateCopy: Date = null;
  noOfBrokersIncluded: { type: string; message: string; iserror: boolean };
  tabIndexObj = ["byDate", "byBroker"];
  reaffirmedIncludedBrokerList = [];
  selectedBrokersListByGrid = [];
  disableAdvanceCalculation: boolean = false;
  eventsDataObj: any;
  relativeDaysControl: UntypedFormControl = new UntypedFormControl();
  constructor(
    private appService: AppService,
    private ccService: CustomConsensusService,
    private eventsService: EventsPageDataService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([infoCircle]);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.ccVars.currentValue) {
      return;
    }

    // This check is for actions when CC is opened in xl addin
    if (this.showHideBrokerCheckbox === false) {
      if (changes.ccVars.currentValue !== changes.ccVars.previousValue) {
        const prevTicker = changes.ccVars.previousValue
          ? changes.ccVars.previousValue.ticker
          : undefined;
        if (
          changes.ccVars.currentValue.ticker &&
          changes.ccVars.currentValue.ticker !== prevTicker
        ) {
          this.getEventsData();
        }
      }
    }

    //create an inital list of selected brokers' source id
    this.ngModel.selectedBrokersByGrid = this.ccVars.brokerlist
      .filter((b) => b.selected_cc)
      .map((b) => b.sourceid);

    //even if cc was applied by date. selected brokers are stored similaryly using flag on brokerlist
    //hence using same list as selectedBrokersByGrid
    this.ngModel.selectedBrokersByDate = this.appService.superClone(
      this.ngModel.selectedBrokersByGrid,
    );

    this.ngModel.showOnlyCurrent = this.ccVars.showOnlyCurrentCc;
    this.ngModel.sortColumn = this.ccVars.sortColumn;
    this.ngModel.sortDirection = this.ccVars.sortDirection;
    this.ngModel.includeNonCurrentBrokers =
      this.ccVars.includeNonCurrentBrokers;
    this.ngModel.includeNonentitledCurrent =
      this.ccVars.includeNonentitledCurrent;
    this.ngModel.includeNonentitledNoncurrent =
      this.ccVars.includeNonentitledNoncurrent;
    this.ngModel.relativeDays = this.ccVars.relativeDays;
    this.ngModel.customConsensusMode = this.ccVars.customConsensusMode;
    this.ngModel.dateCustomConsensus = this.ccVars.dateCustomConsensus;
    this.customConsensusDateCopy = null;
    this.ngModel.customConsensusDateType = this.ccVars.customConsensusDateType;
    this.ngModel.customConsensusBrokerType =
      this.ccVars.customConsensusBrokerType;

    if (this.ccVars.dateCustomConsensus) {
      this.customConsensusDateCopy = this.appService.getDateObjectFrom(
        this.ccVars.dateCustomConsensus,
        "YYYY-MM-DD",
      );
    }
    this.ngModelCopy = this.appService.superClone(this.ngModel);
    // set set correct active tab
    const previouslyAppliedByBroker = this.getPreviouslyAppliedByBroker();
    this.activeTabIdx = Number(
      previouslyAppliedByBroker ||
        this.ngModelCopy.customConsensusMode === "byBroker",
    );
    this.relativeDaysControl.setValue(this.ngModelCopy.relativeDays);
    this.showNoOfBrokers();
  }

  ngOnInit() {
    this.ccService.isCcModalOpen$.subscribe((data) => {
      if (data && this.eventsDataObj === undefined) {
        this.getEventsData();
      }
    });
    if (
      this.ngModelCopy &&
      this.ngModelCopy.customConsensusBrokerType === "reaffirm"
    ) {
      this.disableAdvanceCalculation = true;
    }
    this.relativeDaysControl.valueChanges
      .pipe(debounceTime(800))
      .subscribe((val) => {
        this.ngModelCopy.relativeDays = val;
        if (this.ngModelCopy.customConsensusDateType === "relative") {
          if (val > 180) {
            this.noOfBrokersIncluded = {
              type: this.ngModelCopy.customConsensusDateType,
              iserror: true,
              message: "Max value allowed is 180",
            };
            this.ngModelCopy = {
              ...this.ngModelCopy,
              selectedBrokersByDate: [],
            };
            return;
          }
          this.handleRelativeDaysChange(this.ngModelCopy.relativeDays);
        }
      });
  }

  /**
   * Fired on every source checkbox change on source table
   * @param param0 : {selection: number[]}
   */
  handleSourceSelectionChange = ({ selection }) => {
    this.selectedBrokersListByGrid = selection;
    this.ngModelCopy = {
      ...this.ngModelCopy,
      selectedBrokersByGrid: selection,
    };
  };

  /**
   * Fired on show only current toggle on source table
   * @param param0 : {showOnlyCurrent: boolean}
   */
  handleSourceShowOnlyCurrentChange = ({ showOnlyCurrent }) => {
    this.ngModelCopy = {
      ...this.ngModelCopy,
      showOnlyCurrent: showOnlyCurrent,
    };
  };

  /**
   * Fired on name or modeldate colum sort change on source table
   * @param param0 {sortColumn: string, sortDirection: "ascend" | "descend" | null}
   */
  handleSourceSortChange = ({ sortColumn, sortDirection }) => {
    this.ngModelCopy = {
      ...this.ngModelCopy,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
    };
  };

  /**
   * Fired on nonentitled sources inclusion/exclusion checkboxes are toggled
   * Fired on hide non cc brokers in grid checkbox is toggled
   * @param key chekbox model variable name; passed from template
   * @param checked checkbox current state
   */
  handleCheckbox = (key: string, checked: boolean) => {
    this.ngModelCopy = {
      ...this.ngModelCopy,
      [key]: checked,
    };
  };

  /**
   * Fired on hitting save button
   */
  handleSave = () => {
    //save any changes made on ngModelCopy to ngModel
    this.ngModel = this.ngModelCopy;
    this.onSave.emit(this.ngModelCopy);
  };

  /**
   * Fired on hitting cancel button
   */
  handleCancel = () => {
    //discard any changes on ngModelCopy. bring in original model values
    //discarding handled inside parent when it listens to below cancel emit

    this.onCancel.emit(this.ngModelCopy);
  };

  /**
   * Fired on toggle of checkbox by user in brokerlist grid
   */
  handleBrokerListToggle() {
    this.ngModelCopy = {
      ...this.ngModelCopy,
      customConsensusBrokerType: "brokerlist",
    };
    this.disableAdvanceCalculation = false;
  }

  /**
   * Fired on tab change by user
   * @param index tabIndex; 0 => byDate, 1 => byBroker
   */
  onSelectedTabIndexChange(index: any): void {
    this.activeTabIdx = index;
    const mode = this.tabIndexObj[index];
    this.ngModelCopy = {
      ...this.ngModelCopy,
      customConsensusMode: mode,
    };
    if (
      index === 1 &&
      this.ngModelCopy.customConsensusBrokerType === "reaffirm"
    ) {
      this.disableAdvanceCalculation = true;
      this.getBrokerAfterExcludingReaffirmed();
    }
    this.ngModelChange.emit(this.ngModelCopy);
  }

  /**
   * Fired on by broker radio button change
   * @param value reaffirm / brokerlist
   */
  onCcBrokerRadioButtonChange(value: string): void {
    this.ngModelCopy = {
      ...this.ngModelCopy,
      customConsensusBrokerType: value,
    };
    if (value === "reaffirm") {
      this.disableAdvanceCalculation = true;
      this.getBrokerAfterExcludingReaffirmed();
    } else {
      this.disableAdvanceCalculation = false;
      this.ngModelCopy = {
        ...this.ngModelCopy,
        selectedBrokersByGrid: this.selectedBrokersListByGrid,
      };
    }
  }

  getBrokerAfterExcludingReaffirmed() {
    this.ccService
      .getReaffirmedCCSources(this.ccVars.ticker, window.pg, window.cut)
      .subscribe((response) => {
        this.reaffirmedIncludedBrokerList = response["data"].incl_sources;
        this.ngModelCopy = {
          ...this.ngModelCopy,
          // customConsensusBrokerType: value,
          selectedBrokersByGrid: this.reaffirmedIncludedBrokerList,
          reaffirmBrokers: this.reaffirmedIncludedBrokerList,
          includeNonentitledCurrent: false,
          includeNonentitledNoncurrent: false,
        };
      });
  }

  /**
   * Fired on radio button change
   * @param value custom / relative
   */
  onCcRadioButtonChange(value: string): void {
    this.relativeDaysControl.setValue(null);
    this.customConsensusDateCopy = null;
    this.noOfBrokersIncluded = null;

    this.ngModelCopy = {
      ...this.ngModelCopy,
      dateCustomConsensus: null,
      relativeDays: null,
      customConsensusDateType: value,
    };
  }

  /**
   * Fired on any input in date picker / relative date
   * @param date
   */
  getNoOfBrokersIncluded(date: Date) {
    this.noOfBrokersIncluded = null;
    if (!date) {
      return;
    }
    const refDate = dayjs(date).format("YYYYMMDD");
    const effectiveDate = dayjs(this.ccVars.revisionsAvailableFrom).format(
      "YYYYMMDD",
    );
    let error = false;

    if (refDate < effectiveDate) {
      this.noOfBrokersIncluded = {
        type: this.ngModelCopy.customConsensusDateType,
        iserror: true,
        message: "Revisions unavailable from this date",
      };
      error = true;

      this.ngModelCopy = {
        ...this.ngModelCopy,
        selectedBrokersByDate: [],
      };
    }

    if (!error) {
      this.ccService
        .getTimeBasedCCSources(
          refDate,
          this.ccVars.ticker,
          this.ngModelCopy.includeNonCurrentBrokers ? 0 : 1,
        )
        .pipe(
          catchError((err) => {
            console.error(err);
            return throwError(err);
          }),
        )
        .subscribe((response: any) => {
          if (
            !response.custom_sourceid_list ||
            (response.custom_sourceid_list &&
              response.custom_sourceid_list.length <= 0)
          ) {
            this.noOfBrokersIncluded = {
              type: this.ngModelCopy.customConsensusDateType,
              iserror: true,
              message: "No brokers available from this date",
            };
            error = true;

            this.ngModelCopy = {
              ...this.ngModelCopy,
              selectedBrokersByDate: [],
            };
          }

          if (!error && response && response["status"] === "success") {
            const msg =
              response.custom_sourceid_list &&
              response.custom_sourceid_list.length > 0
                ? `${response.custom_sourceid_list.length} Brokers included`
                : "";
            this.noOfBrokersIncluded = {
              type: this.ngModelCopy.customConsensusDateType,
              iserror: false,
              message: msg,
            };

            this.ngModelCopy = {
              ...this.ngModelCopy,
              selectedBrokersByDate: response.custom_sourceid_list,
            };
          }
        });
    }
  }

  /**
   * Fired on any input in relative date
   * @param relativeDays
   */
  handleRelativeDaysChange = (relativeDays: number) => {
    this.noOfBrokersIncluded = null;

    if (!relativeDays) {
      this.ngModelCopy = {
        ...this.ngModelCopy,
        relativeDays: null,
        dateCustomConsensus: null,
      };
      return;
    }

    const prior = new Date(
      new Date().setDate(new Date().getDate() - relativeDays),
    );
    this.getNoOfBrokersIncluded(prior);
    this.ngModelCopy = {
      ...this.ngModelCopy,
      relativeDays: relativeDays,
      dateCustomConsensus: null,
    };
  };

  /**
   * Fired on any input in custom date
   * @param date
   */
  handleCustomDateChange(date: Date) {
    this.noOfBrokersIncluded = null;

    if (!date) {
      this.ngModelCopy = {
        ...this.ngModelCopy,
        dateCustomConsensus: null,
      };
      return;
    }

    this.getNoOfBrokersIncluded(date);
    const ccDate = date ? dayjs(date).format("YYYYMMDD").toString() : null;
    this.ngModelCopy = {
      ...this.ngModelCopy,
      dateCustomConsensus: ccDate,
    };
    this.ngModelCopy = this.appService.superClone(this.ngModelCopy);

    //feature logging
    const feature = "OPTIONS_REVISION_DATE";
    const extras = {
      REVISION_ORDER: 0,
      REVISION_VALUE: this.appService.getFormattedDateDifference(date),
      URL: window.location.href,
      UTCTIME: this.appService.getCurrentUTCTime(),
    };
    this.ccService.logUserAction(feature, extras);
  }

  onIncludeNonCurrentBrokersCheck(event): void {
    this.handleCheckbox("includeNonCurrentBrokers", event);
    this.showNoOfBrokers();
  }

  showNoOfBrokers() {
    if (this.ngModelCopy.customConsensusDateType === "custom") {
      this.getNoOfBrokersIncluded(this.customConsensusDateCopy);
    } else {
      if (this.ngModelCopy.customConsensusDateType === "relative") {
        if (this.ngModelCopy.relativeDays > 180) {
          this.noOfBrokersIncluded = {
            type: this.ngModelCopy.customConsensusDateType,
            iserror: true,
            message: "Max value allowed is 180",
          };
          this.ngModelCopy = {
            ...this.ngModelCopy,
            selectedBrokersByDate: [],
          };
          return;
        }
        this.handleRelativeDaysChange(this.ngModelCopy.relativeDays);
      }
    }
  }

  onEffectiveStartClick = () => {
    const effectiveDate = dayjs(
      this.ccVars.revisionsAvailableFrom,
      "YYYY-MM-DD",
    ).toDate();
    this.customConsensusDateCopy = effectiveDate;
    const ccDate = dayjs(effectiveDate).format("YYYYMMDD").toString();
    this.ngModelCopy = {
      ...this.ngModelCopy,
      dateCustomConsensus: ccDate,
    };
    this.getNoOfBrokersIncluded(effectiveDate);
  };

  disabledDate = (current: Date): boolean => {
    const today = dayjs().subtract(1, "day");
    const selected = dayjs(current);
    const start =
      this.ccVars.revisionsAvailableFrom !== null
        ? dayjs(this.ccVars.revisionsAvailableFrom, "YYYY-MM-DD")
        : dayjs(0);

    return !(selected < today && selected >= start);
  };

  getEventsData() {
    const prior = new Date(new Date().setDate(new Date().getDate() - 90));
    const eventParamsObj = {
      start_date: dayjs(prior).format("YYYY-MM-DD"),
      end_date: dayjs(new Date()).format("YYYY-MM-DD"),
      event_type: [4, 3450, 3504, 8],
    };
    this.eventsService
      .getEventsMeta(this.ccVars.ticker)
      .pipe(
        catchError((err) => {
          console.error(err);
          return throwError(err);
        }),
      )
      .subscribe((resp) => {
        if (resp.status === "success") {
          const companySymbologyId = resp.symbology_companyid;
          const fromCustomConsensus = true;
          this.eventsService
            .getEvents(companySymbologyId, eventParamsObj, fromCustomConsensus)
            .subscribe((resp) => this.setEventsDate(resp.events));
        }
      });
  }

  setEventsDate(eventsData: any): void {
    this.eventsDataObj = {};
    eventsData.forEach((el) => {
      const startDate = dayjs(el.event_start_date).format("YYYYMMDD");

      if (this.eventsDataObj[startDate]) {
        this.eventsDataObj[startDate].text = `${
          this.eventsDataObj[startDate].text
        }\n${this.setEventText(el)}`;
      } else {
        this.eventsDataObj[startDate] = {
          date: startDate,
          text: this.setEventText(el),
        };
      }
    });
  }

  setEventText(el: any): string {
    let text = "";
    if (el) {
      text = text.concat(
        el.publisher && el.publisher.publisher_name
          ? el.publisher.publisher_name
          : "",
      );
      text = text.concat(` - ${el.event_type}, `);
      text = text.concat(
        el.event_start_date ? dayjs(el.event_start_date).format("Do MMM") : "",
      );
      if (
        (el.event_start_date &&
          dayjs(el.event_start_date).format("yyyymmdd")) !==
        (el.event_end_date && dayjs(el.event_end_date).format("yyyymmdd"))
      ) {
        text = text.concat(" to ");
        text = text.concat(
          el.event_end_date ? dayjs(el.event_end_date).format("Do MMM") : "",
        );
      }
    }
    return text;
  }

  //get rid of this method and its usages. Note: Please do not use this method. its meant to be removed later
  getPreviouslyAppliedByBroker = () => {
    //'previouslyAppliedByBroker' are only for handling cases where user settings were saved
    //before cc 2.0 was developed and so does not contain key 'customConsensusMode'
    //usage 'previouslyAppliedByBroker' can safely be reomoved from below line in future
    //ref bug: CACHE-3181

    const previouslyAppliedByBroker =
      (this.ngModel.selectedBrokersByGrid.length !== 0 ||
        this.ngModel.includeNonentitledCurrent ||
        this.ngModel.includeNonentitledNoncurrent) && //atleast some broker selected
      !this.ngModel.dateCustomConsensus &&
      !this.ngModel.relativeDays; //and cc by date not selected

    return previouslyAppliedByBroker;
  };

  onKeypressEvent(event: any) {
    return event.charCode >= 48 && event.charCode <= 57;
  }
}
